import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import emailjs from "emailjs-com";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
};


const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    state: '',
    district: '',
    feedback: ''
  });

  const branches = [
    {
      title: 'Chennai Branch',
      address: '160, Angappa Naickan Street,\nMannadi, Chennai - 600 001',
      contacts: [
        { type: 'phone', value: ' +91 7810017119' },
        { type: 'email', value: 'ganishirt1993@gmail.com' }
      ]
    },
    
  ];

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 
    'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 
    'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 
    'Telangana', 'Tripura', 'Uttarakhand', 'Uttar Pradesh', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli', 
    'Daman and Diu', 'Delhi', 'Lakshadweep', 'Puducherry'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  try {
    const response = await fetch("https://ganishirts.com/backend/feedback_api.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    const text = await response.text();
    console.log("Raw PHP Response:", text);

    let result;
    try {
      result = JSON.parse(text);
    } catch (err) {
      alert("❌ Invalid JSON response from PHP: " + text);
      return;
    }

    if (result.success) {
      // ✅ Send email via EmailJS
      emailjs.send(
        "service_v17y6ca",   // your EmailJS service ID
        "template_yi7igpj",  // your EmailJS template ID
        {
          name: `${formData.firstName} ${formData.lastName}`, // {{name}}
          email: formData.email,                             // {{email}}
          phone: formData.phoneNumber,                       // {{phone}}
          state: formData.state,                             // {{state}}
          district: formData.district,                       // {{district}}
          message: formData.feedback,                        // {{message}}
          time: new Date().toLocaleString(),                 // {{time}}
        },
        "NRGrt42US8ucOQUCU" // your EmailJS public key
      ).then(
        () => {
          alert("✅ Feedback submitted & email sent successfully");
        },
        (error) => {
          console.error("EmailJS Error:", error);
          alert("⚠️ Feedback stored but email sending failed");
        }
      );

      // reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        state: "",
        district: "",
        feedback: ""
      });
    } else {
      alert("❌ Error: " + result.message);
    }
  } catch (error) {
    console.error("Error:", error);
    alert("Something went wrong. Please try again.");
  }
};



  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-r from-gray-900 to-gray-800 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('/pexels-photo-1.jpeg')" }}
        />

        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-white mb-4"
          >
            Contact Us
          </motion.h1>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            transition={{ duration: 0.7, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-[14px] md:text-base  text-gray-300 max-w-2xl mx-auto"
          >
            We'd love to hear from you! Whether you have a question about our products, 
            branches, or feedback, feel free to reach out to us
          </motion.p>
        </div>
      </section>


      {/* Branches Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Branches
            </h2>
            <p className="text-[14px] md:text-base text-gray-600 max-w-2xl mx-auto">
              Visit us at our different locations or get in touch with any of our branches for assistance
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8"
            >
              <div className="text-center">
                <h3 className="text-[20px] font-bold text-yellow-600 mb-6">
                  {branches[0].title}
                </h3>

                <div className="mb-6">
                  <div className="flex items-start justify-center gap-3 mb-4">
                    <MapPin className="w-5 h-5 text-yellow-600 mt-1 flex-shrink-0" />
                    <div className="text-[14px] text-gray-700 whitespace-pre-line text-left">
                      {branches[0].address}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {branches[0].contacts.map((contact, i) => (
                    <div key={i} className="flex items-center justify-center gap-3">
                      {contact.type === "phone" && (
                        <>
                          <Phone className="w-5 h-5 text-yellow-600" />
                          <a
                            href={`tel:${contact.value}`}
                            className="text-[14px] text-blue-600 hover:underline font-medium"
                          >
                            {contact.value}
                          </a>
                        </>
                      )}
                      {contact.type === "email" && (
                        <>
                          <Mail className="w-5 h-5 text-yellow-600" />
                          <a
                            href={`mailto:${contact.value}`}
                            className="text-[14px] text-blue-600 hover:underline font-medium"
                          >
                            {contact.value}
                          </a>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Map and Feedback Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map */}
             <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="overflow-hidden"
            >
              <div className="p-2 border-b">
                <h3 className="text-2xl font-bold text-gray-900">Find Us</h3>
               {/* <p className="text-gray-600">Chennai Branch Location</p>*/}
              </div>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d971.5209634014718!2d80.2902791678895!3d13.09387200181381!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a526f4f8427741d%3A0xe139f7098935ee71!2s160%2C%20Angappa%20Naicken%20St%2C%20Mannadi%2C%20George%20Town%2C%20Chennai%2C%20Tamil%20Nadu%20600001!5e0!3m2!1sen!2sin!4v1756380073460!5m2!1sen!2sin"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </motion.div>

            {/* Feedback Form */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              transition={{ duration: 0.7, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Send us a Message</h3>
                <p className="text-[14px] text-gray-600">We'd love to hear your feedback and suggestions</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-2">
                      State <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors"
                    >
                      <option value="">Select State</option>
                      {indianStates.map((state) => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="district" className="block text-sm font-semibold text-gray-700 mb-2">
                      District <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="district"
                      name="district"
                      value={formData.district}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your district"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="feedback" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="feedback"
                    name="feedback"
                    value={formData.feedback}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    placeholder="Write your message here..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-vertical transition-colors"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-yellow-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-yellow-700 transition-all duration-300 hover:scale-105"
                >
                  Send Message
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;